import { useQuery } from '@tanstack/react-query'
import { useBlocker } from '@tanstack/react-router'
import { AlertTriangle, CheckCircle2, Info, Loader2, RotateCcw, Save, X } from 'lucide-react'
import { memo, useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'

import { actionsQuery, getAction, getRule, rulesQuery, stripServerFields, validationIssues, validationMessages, type ActionDef, type InteractionRule, type RegistryKind } from '@/api/actions'
import { isApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'

import { useCreateDoc, useSaveDoc } from '../hooks'
import { ActionForm, type Update } from './ActionForm'
import { EditorProvider, useStable, type ActionChoice, type EditorContextValue, type SharedLists } from './context'
import { asObj, ID_RE, isObj, pretty, type Obj } from './doc'
import { OptionList } from './fields'
import { describeIssuePath, normalizeIssues, scrollToPath, type Issue } from './issues'
import { useActionsMeta } from './meta'
import { eventOptions, refOptions } from './refs'
import { registryPlace, useResetFlow } from './ResetDialog'
import { RuleForm } from './RuleForm'
import { useLiveValidation } from './useValidation'

/**
 * The manual editor for one action or world rule (docs/SKEL-STORY.md §9 + §11): a form that needs
 * no JSON, and an Advanced tab with the same document as JSON — both edit one draft, so they never
 * disagree. While you edit, the draft is checked by the server; each problem shows next to its
 * field and in a list at the top that jumps there. Save sends `PUT {base_rev, body}` (a built-in
 * becomes the parent's own copy), or `POST` for a new one; a 409 offers the latest.
 */

type Doc = ActionDef | InteractionRule

export interface DocEditorProps {
  kind: RegistryKind
  mode: 'edit' | 'new'
  /** The server document (edit) or a blank (new). */
  doc: Obj
  onSaved?(doc: Doc): void
  /** Leave the editor (asks first when there are unsaved changes). */
  onClose?(): void
  /** After a 409: take the server's latest (the parent refetches; the editor restarts from it). */
  onReload?(): void
  /** Test seam: how long after the last change the draft is checked. */
  validateDelay?: number
}

interface Section {
  path: string
  label: string
  keys: string[]
}

const ACTION_SECTIONS: Section[] = [
  { path: '', label: 'Basics', keys: ['', 'id', 'label', 'description', 'category', 'idea_template', 'enabled', 'duration'] },
  { path: 'roles', label: 'Who takes part', keys: ['roles'] },
  { path: 'preconditions', label: 'Checks', keys: ['preconditions'] },
  { path: 'program', label: 'Steps', keys: ['program'] },
  { path: 'result', label: 'Afterwards', keys: ['result'] },
  { path: 'clips', label: 'Moves', keys: ['clips'] },
  { path: 'fallback', label: 'If it can’t', keys: ['fallback'] },
  { path: 'fx_auto', label: 'Effects', keys: ['fx_auto'] },
]

const RULE_SECTIONS: Section[] = [
  { path: '', label: 'Basics', keys: ['', 'id', 'label', 'description', 'enabled', 'priority', 'once_per_pair'] },
  { path: 'when', label: 'When', keys: ['when'] },
  { path: 'then', label: 'Then', keys: ['then'] },
  { path: 'result', label: 'Afterwards', keys: ['result'] },
  { path: 'supersedes', label: 'Replaces', keys: ['supersedes'] },
]

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`
const NO_ISSUES: Issue[] = []

export function DocEditor({ kind, mode, doc, onSaved, onClose, onReload, validateDelay }: DocEditorProps) {
  const vocab = useActionsMeta()
  const noun = kind === 'rule' ? 'world rule' : 'action'
  const baseline = useMemo(() => stripServerFields(doc), [doc])
  const baseKey = useMemo(() => JSON.stringify(baseline), [baseline])
  const [draft, setDraft] = useState<Obj>(baseline)
  const [tab, setTab] = useState<'form' | 'json'>('form')
  const [jsonText, setJsonText] = useState('')
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [saveIssues, setSaveIssues] = useState<{ key: string; issues: Issue[] } | null>(null)
  const [conflict, setConflict] = useState<{ currentRev: number | null } | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [asking, setAsking] = useState(false)
  const [scrollTo, setScrollTo] = useState<string | null>(null)
  // A blank does not greet anyone with problems: they show after the first change (or a save).
  const [tried, setTried] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const key = useMemo(() => JSON.stringify(draft), [draft])
  const dirty = key !== baseKey
  // Leaving with unsaved changes asks first (a link, the nav, Back, closing the tab) — except when
  // the editor itself leaves: after a save, or after "Throw them away".
  const leaving = useRef(false)
  const unsaved = useRef(false)
  unsaved.current = dirty
  // Stable functions: `useBlocker` re-registers whenever they change, which would drop a pending ask.
  const shouldBlockFn = useCallback(
    ({ current, next }: { current: { pathname: string; search: unknown }; next: { pathname: string; search: unknown } }) =>
      unsaved.current && !leaving.current && (current.pathname !== next.pathname || JSON.stringify(current.search) !== JSON.stringify(next.search)),
    [],
  )
  const enableBeforeUnload = useCallback(() => unsaved.current && !leaving.current, [])
  const blocker = useBlocker({ shouldBlockFn, enableBeforeUnload, withResolver: true })
  const check = useLiveValidation(kind, draft, mode, validateDelay, mode === 'edit' && typeof doc.id === 'string' ? doc.id : undefined)
  // Stable while unchanged, so the memoised parts of the form don't re-render on every key press.
  const allErrors = useStable(useMemo(() => normalizeIssues([...(saveIssues?.key === key ? saveIssues.issues : []), ...check.errors], draft), [saveIssues, key, check.errors, draft]))
  const quiet = mode === 'new' && !dirty && !tried
  const errors = quiet ? NO_ISSUES : allErrors
  const warnings = useStable(useMemo(() => (quiet ? NO_ISSUES : normalizeIssues(check.warnings, draft)), [quiet, check.warnings, draft]))
  const update = useCallback<Update>((fn) => {
    setDraft((d) => fn(d))
    setFailure(null)
  }, [])
  const listBase = useId()
  const lists = useMemo<SharedLists>(
    () => ({
      capabilities: `${listBase}-caps`,
      clips: `${listBase}-clips`,
      refs: `${listBase}-refs`,
      refsWithFaces: `${listBase}-faces`,
      roleRefs: `${listBase}-roles`,
      events: `${listBase}-events`,
      stateKeys: `${listBase}-state`,
    }),
    [listBase],
  )

  const actionsList = useQuery({ ...actionsQuery({}), enabled: kind === 'action' })
  const rulesList = useQuery({ ...rulesQuery({}), enabled: kind === 'rule' })
  // Ids in use, and the ones the API keeps for itself (`meta`, `validate`…): a new id avoids both.
  const takenIds = useMemo(
    () => [...((kind === 'rule' ? rulesList.data : actionsList.data) ?? []).map((d) => d.id), ...vocab.meta.reserved_ids],
    [kind, rulesList.data, actionsList.data, vocab.meta.reserved_ids],
  )
  const actions = useMemo<ActionChoice[]>(() => (actionsList.data ?? []).map((a) => ({ id: a.id, label: a.label, roles: Object.keys(a.roles ?? {}) })), [actionsList.data])
  const roleKey = kind === 'rule' ? (isObj(asObj(draft.when).other) ? 'subject,other' : 'subject') : Object.keys(asObj(draft.roles)).join(',')
  const roles = useMemo(() => (roleKey ? roleKey.split(',') : []), [roleKey])
  const selfId = typeof draft.id === 'string' ? draft.id : ''
  const ctx = useMemo<EditorContextValue>(
    () => ({ kind, meta: vocab.meta, meanings: vocab.meanings, roles, errors, warnings, actions, selfId, lists }),
    [kind, vocab, roles, errors, warnings, actions, selfId, lists],
  )

  const save = useSaveDoc(kind)
  const create = useCreateDoc(kind)
  const place = mode === 'edit' ? registryPlace(doc) : 'own'
  const title = String(draft.label || draft.id || (kind === 'rule' ? 'the world rule' : 'the action'))
  const reset = useResetFlow(kind, String(doc.id ?? ''), title)
  // A new one needs at least a name (and the id made from it) whatever the checker says.
  const nameless = mode === 'new' && (!String(draft.label ?? '').trim() || !ID_RE.test(String(draft.id ?? '')))
  const blocked = saving || jsonError !== null || allErrors.length > 0 || nameless || (mode === 'edit' && !dirty)

  // Jump to a field once the form tab is on screen.
  useEffect(() => {
    if (tab !== 'form' || scrollTo === null) return
    const target = scrollTo
    setScrollTo(null)
    setTimeout(() => {
      if (rootRef.current) scrollToPath(rootRef.current, target)
    }, 0)
  }, [tab, scrollTo])

  const goTo = (path: string): void => {
    if (tab !== 'form') setTab('form')
    setScrollTo(path)
  }

  const doSave = async (baseRevOverride?: number): Promise<void> => {
    if (saving) return
    setTried(true)
    setSaving(true)
    setFailure(null)
    setConflict(null)
    const sentKey = key
    try {
      const saved =
        mode === 'new'
          ? await create.mutateAsync(draft)
          : await save.mutateAsync({ id: String(doc.id), baseRev: baseRevOverride ?? (typeof doc.rev === 'number' ? doc.rev : 0), body: draft })
      toast.success(mode === 'new' ? `Made “${title}”` : 'Saved', place === 'builtin' ? { description: 'Saved as your own copy. The original is kept.' } : undefined)
      // A new one opens on its own page next: nothing is left unsaved.
      if (mode === 'new') leaving.current = true
      onSaved?.(saved)
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        if (mode === 'new') setSaveIssues({ key: sentKey, issues: [{ path: 'id', message: 'That name is already used. Pick another one.' }] })
        else setConflict({ currentRev: typeof err.details.current_rev === 'number' ? err.details.current_rev : null })
      } else if (isApiError(err) && err.status === 422) {
        setSaveIssues({ key: sentKey, issues: validationIssues(err) })
      } else {
        setFailure(validationMessages(err).join(' '))
      }
    } finally {
      setSaving(false)
    }
  }

  const saveAnyway = async (): Promise<void> => {
    let rev = conflict?.currentRev ?? null
    if (rev === null) {
      try {
        const head = kind === 'rule' ? await getRule(String(doc.id)) : await getAction(String(doc.id))
        rev = head.rev
      } catch (err) {
        setFailure(validationMessages(err).join(' '))
        return
      }
    }
    await doSave(rev)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
      e.preventDefault()
      if (!blocked) void doSave()
    }
  }

  const undo = (): void => {
    setDraft(baseline)
    setSaveIssues(null)
    setJsonText(pretty(baseline))
    setJsonError(null)
  }

  const close = (): void => {
    if (dirty) setAsking(true)
    else onClose?.()
  }

  const sections = kind === 'rule' ? RULE_SECTIONS : ACTION_SECTIONS
  const countIn = (s: Section): number => errors.filter((e) => s.keys.includes(e.path.split('.')[0] ?? '')).length

  return (
    <div ref={rootRef} className="flex min-w-0 flex-col gap-4" data-testid="doc-editor" data-kind={kind} data-mode={mode} onKeyDown={onKeyDown}>
      {mode === 'edit' && place === 'builtin' ? (
        <p className="flex items-start gap-2 rounded-xl border border-info/30 bg-info-soft p-3 text-sm text-ink" data-testid="builtin-note">
          <Info className="mt-0.5 size-4 shrink-0 text-info" aria-hidden />
          <span>This {noun} is built in. Saving makes your own copy — the original is kept, and you can go back to it any time.</span>
        </p>
      ) : null}
      {mode === 'edit' && place === 'changed' ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-line bg-paper-2 p-3 text-sm text-ink" data-testid="changed-note">
          <span>This is your changed copy of a built-in {noun}.</span>
          <Button variant="secondary" size="sm" onClick={reset.open}>
            <RotateCcw aria-hidden />
            Reset to original
          </Button>
        </div>
      ) : null}

      <EditorProvider value={ctx}>
        <SharedDatalists lists={lists} roles={roles} />
        <Tabs value={tab} onValueChange={(v) => {
          if (v === 'json') {
            setJsonText(pretty(draft))
            setJsonError(null)
          } else setJsonError(null)
          setTab(v as 'form' | 'json')
        }}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList aria-label="How to edit">
              <TabsTrigger value="form" data-testid="tab-form">
                Form
              </TabsTrigger>
              <TabsTrigger value="json" data-testid="tab-json">
                Advanced (JSON)
              </TabsTrigger>
            </TabsList>
            {tab === 'form' ? (
              <nav aria-label="Parts of the form" className="flex flex-wrap gap-1">
                {sections.map((s) => {
                  const n = countIn(s)
                  return (
                    <button
                      key={s.path || 'basics'}
                      type="button"
                      className={cn('inline-flex h-8 items-center gap-1 rounded-full border px-2.5 text-xs font-medium focus-visible:focus-ring', n ? 'border-danger/40 bg-danger-soft text-danger' : 'border-line bg-paper text-ink-2 hover:bg-paper-2 hover:text-ink')}
                      onClick={() => goTo(s.path)}
                    >
                      {s.label}
                      {n ? <span aria-label={plural(n, 'problem', 'problems')}>· {n}</span> : null}
                    </button>
                  )
                })}
              </nav>
            ) : null}
          </div>

          {errors.length > 0 ? <ErrorSummary errors={errors} kind={kind} onGo={goTo} /> : null}

          <TabsContent value="form" className="pt-2">
            {kind === 'rule' ? <RuleForm doc={draft} update={update} mode={mode} takenIds={takenIds} /> : <ActionForm doc={draft} update={update} mode={mode} takenIds={takenIds} />}
          </TabsContent>
          <TabsContent value="json" className="pt-2">
            <div className="card flex flex-col gap-3 p-5" data-testid="json-tab">
              <label htmlFor="doc-editor-json" className="text-sm font-medium text-ink">
                The whole {noun} as JSON
              </label>
              <p className="text-xs text-ink-3">For grown-ups who like brackets. Changes here show up in the form, and the other way round.</p>
              <Textarea
                id="doc-editor-json"
                value={jsonText}
                rows={24}
                spellCheck={false}
                className="font-mono text-xs leading-5"
                aria-invalid={jsonError !== null || undefined}
                onChange={(e) => {
                  const text = e.target.value
                  setJsonText(text)
                  try {
                    const v: unknown = JSON.parse(text)
                    if (!isObj(v)) {
                      setJsonError('It must be one JSON object: { … }.')
                      return
                    }
                    setJsonError(null)
                    update(() => stripServerFields(v))
                  } catch (err) {
                    setJsonError(`This is not valid JSON yet: ${err instanceof Error ? err.message : String(err)}`)
                  }
                }}
              />
              {jsonError ? (
                <p className="text-sm text-danger" role="alert" data-testid="json-local-error">
                  {jsonError}
                </p>
              ) : null}
            </div>
          </TabsContent>
        </Tabs>
      </EditorProvider>

      {conflict ? (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-warn/40 bg-warn-soft p-3 text-sm text-ink" role="alert" data-testid="save-conflict">
          <AlertTriangle className="size-4 text-warn" aria-hidden />
          <span className="mr-auto">Someone changed this {noun} while you were editing.</span>
          {onReload ? (
            <Button variant="secondary" size="sm" onClick={onReload}>
              Get the latest
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={() => void saveAnyway()}>
            Save mine anyway
          </Button>
        </div>
      ) : null}
      {failure ? (
        <p className="rounded-xl border border-danger/40 bg-danger-soft p-3 text-sm text-danger" role="alert" data-testid="save-failure">
          It could not be saved: {failure}
        </p>
      ) : null}

      {/* Stays above the queue strip (AppShell publishes its height as --shell-bottom). */}
      <div className="sticky z-10 -mx-1 flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-paper/95 p-3 shadow-pop backdrop-blur" style={{ bottom: 'calc(var(--shell-bottom, 40px) + 0.5rem)' }} data-testid="save-bar">
        {quiet || (nameless && errors.length === 0) ? (
          <p className="flex items-center gap-2 text-sm text-ink-2" role="status" data-testid="editor-status" data-status="blank">
            <Info className="size-4 text-ink-3" aria-hidden />
            {quiet ? 'Give it a name to start.' : 'Give it a name to save it.'}
          </p>
        ) : (
          <StatusLine status={check.status} errors={errors.length} warnings={warnings.length} dirty={dirty} jsonError={jsonError !== null} mode={mode} />
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {onClose ? (
            <Button variant="ghost" onClick={close}>
              <X aria-hidden />
              {mode === 'new' ? 'Cancel' : 'Close editor'}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={undo} disabled={!dirty || saving}>
            <RotateCcw aria-hidden />
            Undo my changes
          </Button>
          <Button variant="primary" onClick={() => void doSave()} disabled={blocked} loading={saving} data-testid="editor-save">
            {saving ? null : <Save aria-hidden />}
            Save
          </Button>
        </div>
      </div>

      {reset.dialog}
      <Dialog
        open={asking || blocker.status === 'blocked'}
        onOpenChange={(o) => {
          if (o) return
          setAsking(false)
          blocker.reset?.()
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Throw away your changes?</DialogTitle>
            <DialogDescription>They have not been saved.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setAsking(false)
                blocker.reset?.()
              }}
            >
              Keep editing
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setAsking(false)
                leaving.current = true
                if (blocker.status === 'blocked') blocker.proceed()
                else onClose?.()
              }}
            >
              Throw them away
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** The suggestion lists every field of this editor shares (rendered once instead of per field). */
const SharedDatalists = memo(function SharedDatalists({ lists, roles }: { lists: SharedLists; roles: string[] }) {
  const { meta, meanings } = useActionsMeta()
  const refs = useMemo(() => refOptions(roles, meta.sockets), [roles, meta.sockets])
  const faces = useMemo(() => refOptions(roles, meta.sockets, { faces: true }), [roles, meta.sockets])
  const roleRefs = useMemo(() => refOptions(roles, meta.sockets, { roleOnly: true }), [roles, meta.sockets])
  const events = useMemo(() => eventOptions(roles, meta.moments), [roles, meta.moments])
  const plain = (xs: readonly string[]) => xs.map((x) => ({ value: x, label: x }))
  return (
    <>
      <OptionList id={lists.capabilities} options={meta.capabilities.map((c) => ({ value: c, label: meanings[c] ?? c }))} />
      <OptionList id={lists.clips} options={plain(meta.clip_names)} />
      <OptionList id={lists.refs} options={refs} />
      <OptionList id={lists.refsWithFaces} options={faces} />
      <OptionList id={lists.roleRefs} options={roleRefs} />
      <OptionList id={lists.events} options={events} />
      <OptionList id={lists.stateKeys} options={plain(meta.state_keys)} />
    </>
  )
})

function StatusLine({ status, errors, warnings, dirty, jsonError, mode }: { status: string; errors: number; warnings: number; dirty: boolean; jsonError: boolean; mode: 'edit' | 'new' }) {
  let icon = <CheckCircle2 className="size-4 text-ok" aria-hidden />
  let text: string
  if (jsonError) {
    icon = <AlertTriangle className="size-4 text-danger" aria-hidden />
    text = 'Fix the JSON first.'
  } else if (errors > 0) {
    icon = <AlertTriangle className="size-4 text-danger" aria-hidden />
    text = `${plural(errors, 'thing', 'things')} to fix before saving.`
  } else if (status === 'checking') {
    icon = <Loader2 className="size-4 animate-spin text-ink-3" aria-hidden />
    text = 'Checking…'
  } else if (status === 'offline') {
    icon = <Info className="size-4 text-ink-3" aria-hidden />
    text = 'It is checked when you save.'
  } else {
    text = dirty || mode === 'new' ? 'Looks good.' : 'No changes yet.'
  }
  return (
    <p className="flex items-center gap-2 text-sm text-ink-2" role="status" data-testid="editor-status" data-status={errors > 0 ? 'invalid' : status}>
      {icon}
      <span>
        {text}
        {warnings > 0 && errors === 0 ? ` ${plural(warnings, 'hint', 'hints')} below.` : ''}
      </span>
    </p>
  )
}

function ErrorSummary({ errors, kind, onGo }: { errors: Issue[]; kind: RegistryKind; onGo(path: string): void }) {
  const id = useId()
  return (
    // A labelled region, not an alert: it changes while you type, and the status line already says how many.
    <section className="mt-3 rounded-2xl border border-danger/40 bg-danger-soft p-4" aria-labelledby={`${id}-title`} data-testid="error-summary">
      <p id={`${id}-title`} className="font-medium text-danger">
        {plural(errors.length, 'thing', 'things')} to fix before saving:
      </p>
      <ul className="mt-2 flex flex-col gap-1">
        {errors.map((e, i) => (
          <li key={`${i}:${e.path}:${e.message}`}>
            <button type="button" className="text-left text-sm text-ink underline-offset-2 hover:underline focus-visible:focus-ring" onClick={() => onGo(e.path)} data-testid="error-link" data-path={e.path}>
              <span className="font-medium">{describeIssuePath(e.path, kind)}:</span> {e.message}
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
